import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, faInfoCircle, faTrash, faCheck, faSpinner,
  faBox, faComment, faRobot, faCopy, faDollarSign, faShieldAlt, faHardDrive,
  faUserPlus, faSearch, faTimes, faCog,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import { useTheme } from '../hooks/useTheme';
import '../styles/Notifications.css';

interface Notification {
  id: string;
  type: 'SYSTEM' | 'ITEM_UPDATE' | 'COMMENT' | 'AI_SUGGESTION' | 'DUPLICATE_DETECTED' | 'VALUE_CHANGED' | 'MENTION' | 'SHARE' | 'SECURITY' | 'STORAGE_WARNING';
  title: string;
  message: string;
  createdAt: string;
  isRead: boolean;
  readAt?: string;
  actionUrl?: string;
}

type FilterType = 'all' | 'unread' | 'ITEM_UPDATE' | 'COMMENT' | 'AI_SUGGESTION' | 'DUPLICATE_DETECTED' | 'VALUE_CHANGED' | 'SECURITY' | 'STORAGE_WARNING' | 'SYSTEM';

const NOTIFICATION_TYPE_CONFIG: Record<string, { icon: any; label: string; color: string; className: string }> = {
  ITEM_UPDATE: { icon: faBox, label: 'Item Updates', color: '#4facfe', className: 'info' },
  COMMENT: { icon: faComment, label: 'Comments', color: '#667eea', className: 'info' },
  AI_SUGGESTION: { icon: faRobot, label: 'AI Suggestions', color: '#38ef7d', className: 'success' },
  DUPLICATE_DETECTED: { icon: faCopy, label: 'Duplicates', color: '#f5576c', className: 'warning' },
  VALUE_CHANGED: { icon: faDollarSign, label: 'Value Changes', color: '#fbc531', className: 'success' },
  SECURITY: { icon: faShieldAlt, label: 'Security', color: '#f5576c', className: 'warning' },
  STORAGE_WARNING: { icon: faHardDrive, label: 'Storage', color: '#fa709a', className: 'warning' },
  MENTION: { icon: faUserPlus, label: 'Mentions', color: '#764ba2', className: 'info' },
  SHARE: { icon: faUserPlus, label: 'Shared', color: '#11998e', className: 'info' },
  SYSTEM: { icon: faInfoCircle, label: 'System', color: '#a0aec0', className: 'info' },
};

const Notifications = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [trashedNotifications, setTrashedNotifications] = useState<Notification[]>([]);
  const [showTrash, setShowTrash] = useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchTrashedNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrashedNotifications = async () => {
    try {
      const response = await notificationsAPI.getTrash();
      setTrashedNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch trashed notifications:', error);
      setTrashedNotifications([]);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(notifications.map(notif =>
        notif.id === id ? { ...notif, isRead: true, readAt: new Date().toISOString() } : notif
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const deleted = notifications.find(n => n.id === id);
      await notificationsAPI.delete(id);
      setNotifications(notifications.filter(notif => notif.id !== id));
      if (deleted) {
        setTrashedNotifications(prev => [{ ...deleted }, ...prev]);
      }
      if (deleted && !deleted.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await notificationsAPI.restore(id);
      const restored = trashedNotifications.find(n => n.id === id);
      setTrashedNotifications(prev => prev.filter(n => n.id !== id));
      if (restored) {
        setNotifications(prev => [{ ...restored }, ...prev]);
        if (!restored.isRead) {
          setUnreadCount(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Failed to restore notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map(notif => ({ ...notif, isRead: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(t('notifications.deleteConfirm'))) return;
    try {
      await notificationsAPI.deleteAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.isRead) {
      handleMarkAsRead(notif.id);
    }
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
    }
  };

  const getTypeConfig = (type: string) => {
    return NOTIFICATION_TYPE_CONFIG[type] || NOTIFICATION_TYPE_CONFIG.SYSTEM;
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo').replace('{count}', String(diffMins));
    if (diffHours < 24) return t('notifications.hoursAgo').replace('{count}', String(diffHours));
    if (diffDays < 7) return t('notifications.daysAgo').replace('{count}', String(diffDays));
    return date.toLocaleDateString();
  };

  const getDayLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const notifDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (notifDate.getTime() === today.getTime()) return t('notifications.today');
    if (notifDate.getTime() === yesterday.getTime()) return t('notifications.yesterday');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Filter notifications
  const sourceNotifications = showTrash ? trashedNotifications : notifications;

  const filteredNotifications = sourceNotifications
    .filter(n => {
      if (showTrash) return true;
      if (activeFilter === 'unread') return !n.isRead;
      if (activeFilter !== 'all') return n.type === activeFilter;
      return true;
    })
    .filter(n => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q);
    });

  // Group by day
  const groupedNotifications: { label: string; items: Notification[] }[] = [];
  filteredNotifications.forEach(notif => {
    const label = getDayLabel(notif.createdAt);
    const group = groupedNotifications.find(g => g.label === label);
    if (group) {
      group.items.push(notif);
    } else {
      groupedNotifications.push({ label, items: [notif] });
    }
  });

  // Get unique types for filter tabs
  const typeCounts: Record<string, number> = {};
  notifications.forEach(n => {
    typeCounts[n.type] = (typeCounts[n.type] || 0) + 1;
  });

  const filterTabs: { key: FilterType; label: string; count?: number }[] = [
    { key: 'all', label: t('notifications.all'), count: notifications.length },
    { key: 'unread', label: t('notifications.unread'), count: unreadCount },
    ...Object.entries(typeCounts).map(([type, count]) => ({
      key: type as FilterType,
      label: NOTIFICATION_TYPE_CONFIG[type]?.label || type,
      count,
    })),
  ];

  if (loading) {
    return (
      <div className="notifications-loading">
        <FontAwesomeIcon icon={faSpinner} spin /> {t('notifications.loading')}
      </div>
    );
  }

  return (
    <div className={`notifications theme-${theme}`}>
      <div className="notifications-header">
        <div className="header-left">
          <h2><FontAwesomeIcon icon={faBell} /> {showTrash ? 'Notification Trash' : t('notifications.title')}</h2>
          {!showTrash && <span className="unread-badge">{unreadCount} {t('notifications.unread').toLowerCase()}</span>}
        </div>
        <div className="notifications-controls">
          <button className="btn-mark-all" onClick={() => setShowTrash(prev => !prev)}>
            <FontAwesomeIcon icon={faTrash} /> {showTrash ? 'Back to Notifications' : 'Open Trash'}
          </button>
          {unreadCount > 0 && (
            <button className="btn-mark-all" onClick={handleMarkAllAsRead}>
              <FontAwesomeIcon icon={faCheck} /> {t('notifications.markAllRead')}
            </button>
          )}
          {!showTrash && notifications.length > 0 && (
            <button className="btn-clear-all" onClick={handleClearAll}>
              <FontAwesomeIcon icon={faTrash} /> {t('notifications.clearAll')}
            </button>
          )}
          <button className="btn-settings" onClick={() => navigate('/notification-settings')} title="Notification Settings">
            <FontAwesomeIcon icon={faCog} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="notifications-search">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          placeholder={t('notifications.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="search-clear" onClick={() => setSearchTerm('')}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      {!showTrash && <div className="notifications-filters">
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            className={`filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
            onClick={() => setActiveFilter(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && <span className="tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>}

      {/* Grouped Notifications */}
      {groupedNotifications.length > 0 ? (
        <div className="notifications-grouped">
          {groupedNotifications.map(group => (
            <div key={group.label} className="notification-group">
              <div className="group-label">{group.label}</div>
              <div className="notifications-list">
                {group.items.map(notif => {
                  const config = getTypeConfig(notif.type);
                  return (
                    <div 
                      key={notif.id} 
                      className={`notification-item ${config.className} ${!notif.isRead ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <div className="notification-icon" style={{ color: config.color }}>
                        <FontAwesomeIcon icon={config.icon} />
                      </div>
                      <div className="notification-content">
                        <div className="notification-title-row">
                          <h4>{notif.title}</h4>
                          <span className="notification-type-badge" style={{ color: config.color }}>
                            {config.label}
                          </span>
                        </div>
                        <p>{notif.message}</p>
                        <span className="notification-time">{formatTimeAgo(notif.createdAt)}</span>
                      </div>
                      <div className="notification-actions" onClick={(e) => e.stopPropagation()}>
                        {!notif.isRead && (
                          <button
                            className="btn-read"
                            onClick={() => handleMarkAsRead(notif.id)}
                            title="Mark as read"
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                        )}
                        <button
                          className="btn-delete"
                          onClick={() => showTrash ? handleRestore(notif.id) : handleDelete(notif.id)}
                          title={showTrash ? 'Restore notification' : 'Move to trash'}
                        >
                          <FontAwesomeIcon icon={showTrash ? faCheck : faTrash} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <FontAwesomeIcon icon={faBell} className="empty-icon" />
          <h3>{showTrash ? 'Trash is empty' : (activeFilter !== 'all' || searchTerm ? t('notifications.noMatching') : t('notifications.noNotifications'))}</h3>
          <p>{showTrash ? 'Deleted notifications will appear here.' : (activeFilter !== 'all' || searchTerm ? t('notifications.tryAdjusting') : t('notifications.allCaughtUp'))}</p>
          {!showTrash && (activeFilter !== 'all' || searchTerm) && (
            <button className="btn-reset-filter" onClick={() => { setActiveFilter('all'); setSearchTerm(''); }}>
              {t('notifications.showAll')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;
